import {useTheme} from '@mui/material';
import {useState} from 'react';

import {QueryParamsEnum} from 'presentation/constants';
import {useContractStatus} from 'presentation/hooks';
import {BookingTypeEnum} from 'presentation/pages/private/booking/constants';
import {AppointmentsHomeStateType} from 'presentation/pages/private/home/types';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {useMyAppointmentsList} from '../../../../hooks/useMyAppointmentsList';
import {FilterProps} from '../../types';
import {useCongenereEligibilityStore} from 'presentation/pages/private/store/useCongenereEligibilityStore';

const {
	all,
	healthAppointment,
	dentist,
	teleHealth,
	exam,

	scheduleType,
	surgery,
} = translations['pt-br'].commons;

export function useAppointmentsFilterHeader(totalCount?: number) {
	const {
		appointmentsList,
		appointmentsListFiltered,
		appointmentsListError,
		appointmentsListLoading,
		appointmentsListFilteredEmpty,
	} = useMyAppointmentsList();

	const {isTablet, isMobile} = useMediaQueries();
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {bookingType} = SEARCH_PARAMS;
	const {isContractSuspended} = useContractStatus();
	const theme = useTheme();

	const [openDrawerState, setOpenDrawerState] = useState(false);
	const [selectedFilterState, setSelectedFilterState] = useState(bookingType);

	const {congenereEligibility} = useCongenereEligibilityStore();

	function isFilterAvailable(
		filterCategory: BookingTypeEnum | string,
		appointments: AppointmentsHomeStateType[],
	): boolean {
		if (!filterCategory) {
			return true;
		}
		return appointments.some(item => item.bookingType === filterCategory);
	}

	const shouldHideComponent =
		appointmentsListError ||
		appointmentsListLoading ||
		appointmentsListFilteredEmpty ||
		!appointmentsListFiltered;

	const filters: FilterProps[] = appointmentsList
		? [
				{
					label: all,
					category: '',
				},
				{
					label: surgery,
					category: BookingTypeEnum.SURGERY,
				},
				{
					label: healthAppointment,
					category: BookingTypeEnum.HEALTH,
				},
				{
					label: dentist,
					category: BookingTypeEnum.ODONTO,
				},
				{
					label: teleHealth,
					category: BookingTypeEnum.TELEHEALTH,
				},
				{
					label: exam,
					category: BookingTypeEnum.EXAM,
				},
			].filter(filter => isFilterAvailable(filter.category, appointmentsList))
		: [];

	const hasFilters = filters.length > 2;

	function handleSelectFilter(type: BookingTypeEnum | string) {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.BOOKING_TYPE, type);
			if (type === '') {
				prev.delete(QueryParamsEnum.BOOKING_TYPE);
			}
			return prev;
		});
	}

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	const handleCloseDrawer = () => {
		setOpenDrawerState(false);
	};

	const listFilters = [
		{
			categoryTitle: scheduleType,
			categoryItemsList: filters.map(filter => ({
				active: selectedFilterState === filter.category,
				title: filter.label,
				onClick: () => setSelectedFilterState(filter.category),
			})),
		},
	];

	const handleApplyFilter = () => {
		handleSelectFilter(selectedFilterState);
		setOpenDrawerState(false);
	};

	const handleClearFilter = () => {
		setSelectedFilterState('');
		handleSelectFilter('');
		setOpenDrawerState(false);
	};

	const ONLY_ONE_APPOINTMENT = totalCount === 1;
	const appointmentCountText = `Total de ${totalCount} agendamento${ONLY_ONE_APPOINTMENT ? '' : 's'}`;

	const shouldShowAppointmentButton = !isContractSuspended && !congenereEligibility;

	return {
		shouldHideComponent,
		appointmentCountText,
		hasFilters,
		handleOpenDrawer,
		listFilters,
		openDrawerState,
		handleCloseDrawer,
		isContractSuspended,
		isMobile,
		theme,
		handleClearFilter,
		handleApplyFilter,
		isTablet,
		shouldShowAppointmentButton,
		bookingType,
	};
}
